import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './Layout.css';
import navBarLogoOld from '../images/nav bar logo-old.jfif';
import iconLogo from '../images/icon_logo.jpg';

function Layout({ children, siteData }) {
  const navItems = siteData?.navItems || [];
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();

  // Close mobile drawer and scroll to top of page when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Monitor scroll position to toggle Scroll to Top button visibility
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 250) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const getItemSubLinks = (item) => {
    if (item.dropdownLinks && item.dropdownLinks.length > 0) return item.dropdownLinks;
    if (item.to === '/about') {
      return [
        { label: 'Overview & Mission', to: '/about' },
        { label: '12 Areas of Focus', to: '/about/areas-of-focus' },
        { label: 'Strategic Pillars & SDGs', to: '/about/strategic-pillars' },
        { label: 'Governance & Leadership', to: '/about/governance' }
      ];
    }
    if (item.to === '/united-nations') {
      return siteData?.unitedNations?.links || [
        { label: 'UN University for Global Peace', to: '/united-nations/un-university-for-global-peace' },
        { label: 'UN Human Rights Declaration', to: '/united-nations/un-human-rights-declaration' },
        { label: 'SDG Interactive', to: '/united-nations/sdg-interactive' }
      ];
    }
    return null;
  };

  return (
    <div className="app-shell">
      {/* Top Utility Bar */}
      <div className="top-bar">
        <div className="top-bar-inner">
          <div className="top-bar-info">
            <span className="info-item">🌐 Delaware, USA (Global HQ)</span>
            <span className="info-divider">|</span>
            <span className="info-item">✉ info@ungdo.org</span>
          </div>
          <div className="top-bar-links">
            <NavLink to="/contact" className="top-link">Contact Head Office</NavLink>
            <NavLink to="/admin" className="top-link admin-link">Portal Login</NavLink>
          </div>
        </div>
      </div>

      <header className="site-header">
        <div className="header-inner">
          <div className="brand-wrap">
            <NavLink to="/" className="brand-link" aria-label="Home">
              {siteData?.logo ? (
                <img className="brand-logo" src={siteData.logo} alt={siteData?.siteName || 'UNGDO'} />
              ) : (
                <div className="logo-circle" aria-hidden="true">U</div>
              )}
            </NavLink>
          </div>

          {/* Desktop Navigation */}
          <nav className="main-nav desktop-nav" aria-label="Main Navigation">
            {navItems.map((item) => {
              const subLinks = getItemSubLinks(item);
              if (subLinks && subLinks.length > 0) {
                return (
                  <div key={item.to} className="nav-item dropdown">
                    <NavLink
                      to={item.to}
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                      {item.label}
                      <span className="dropdown-caret">▾</span>
                    </NavLink>
                    <div className="dropdown-menu" aria-label={`${item.label} submenu`}>
                      {subLinks.map((link) => (
                        <NavLink key={link.to} to={link.to} className="dropdown-link">
                          {link.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          {/* Mobile Hamburger Button */}
          <button
            type="button"
            className={`mobile-menu-toggle ${mobileMenuOpen ? 'open' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle Navigation Menu"
            aria-expanded={mobileMenuOpen}
          >
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </button>
        </div>
      </header>

      {/* Mobile Drawer Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-drawer-backdrop" onClick={toggleMobileMenu} />
      )}

      <nav className={`mobile-drawer ${mobileMenuOpen ? 'open' : ''}`} aria-label="Mobile Navigation">
        <div className="mobile-drawer-header">
          <h3>Menu</h3>
          <button type="button" className="close-drawer" onClick={toggleMobileMenu} aria-label="Close Menu">✕</button>
        </div>
        <div className="mobile-drawer-links">
          {navItems.map((item) => {
            const subLinks = getItemSubLinks(item);
            if (subLinks && subLinks.length > 0) {
              return (
                <div key={item.to} className="mobile-dropdown">
                  <NavLink
                    to={item.to}
                    className={({ isActive }) => (isActive ? 'mobile-nav-link active' : 'mobile-nav-link')}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                  <div className="mobile-sub-links">
                    {subLinks.map((link) => (
                      <NavLink
                        key={link.to}
                        to={link.to}
                        className="mobile-sub-link"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        ↳ {link.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => (isActive ? 'mobile-nav-link active' : 'mobile-nav-link')}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </NavLink>
            );
          })}
        </div>
      </nav>

      <main className="page-content">{children}</main>

      <footer className="site-footer">
        <div className="footer-inner">
          <div className="footer-col brand-col">
            <div className="footer-brand-logos" style={{ marginBottom: '16px' }}>
              <img src={navBarLogoOld} alt="UNGDO Logo" style={{ height: '56px', width: 'auto', borderRadius: '6px', background: '#ffffff', padding: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }} />
            </div>
            <p className="footer-tagline">Empowering communities, supporting peace, and advancing the UN Sustainable Development Goals globally.</p>
          </div>

          <div className="footer-col">
            <h5>Navigation</h5>
            <ul className="footer-links">
              <li><NavLink to="/">Home</NavLink></li>
              <li><NavLink to="/about">About Us</NavLink></li>
              <li><NavLink to="/projects">Projects</NavLink></li>
              <li><NavLink to="/united-nations">United Nations</NavLink></li>
              <li><NavLink to="/augp-usa">AUGP USA</NavLink></li>
            </ul>
          </div>

          <div className="footer-col">
            <h5>UN Initiatives</h5>
            <ul className="footer-links">
              <li><NavLink to="/united-nations/sdg-interactive">SDG Interactive</NavLink></li>
              <li><NavLink to="/united-nations/un-human-rights-declaration">Human Rights Declaration</NavLink></li>
              <li><NavLink to="/united-nations/un-university-for-global-peace">UN University for Global Peace</NavLink></li>
              <li><NavLink to="/contact">Contact Head Office</NavLink></li>
            </ul>
          </div>

          <div className="footer-col" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <div style={{ marginBottom: '12px' }}>
              <img src={iconLogo} alt="UNGDO Icon" style={{ height: '56px', width: 'auto', borderRadius: '6px', background: '#ffffff', padding: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }} />
            </div>
            <h5>Headquarters</h5>
            <p className="footer-address" style={{ textAlign: 'center', margin: '0 0 14px' }}>
              Delaware, USA<br />
              <strong>Email:</strong> <a href="mailto:info@ungdo.org" style={{ color: '#009edb', textDecoration: 'none', fontWeight: '600' }}>info@ungdo.org</a>
            </p>
            <div className="footer-social" style={{ justifyContent: 'center', marginTop: '4px' }}>
              <span>Follow us:</span>
              <a href={siteData?.contact?.facebook || 'https://web.facebook.com/unglobaldevorg'} target="_blank" rel="noreferrer" className="social-pill">
                Facebook Page
              </a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} United Nations Global Development Organization. All Rights Reserved.</p>
        </div>
      </footer>

      {/* Floating Scroll to Top Button */}
      {showScrollTop && (
        <button
          type="button"
          className="scroll-to-top-btn"
          onClick={scrollToTop}
          aria-label="Scroll to top of page"
          title="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  );
}

export default Layout;
